import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { FiDatabase, FiMessageCircle } from 'react-icons/fi';

import { useAuth } from '../../contexts/AuthContext';
import { chatService } from '../../services/aiService';
import ChatWindow from './ChatWindow';
import DocumentViewer from './DocumentViewer';
import SnippetDisplay from './SnippetDisplay';
import SourceList from './SourceList';

// Hauptkomponente
function Chat() {
  const { isDarkMode } = useAuth();

  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content:
        'Hallo! Ich kann Ihnen mit allgemeinen Fragen helfen und greife dabei auf Ihre hochgeladenen Dokumente und Gesprächsverlauf zurück.',
    },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sources, setSources] = useState([]);
  const [snippets, setSnippets] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [chatMode, setChatMode] = useState('documents'); // 'documents' oder 'free'
  const [trainingEnabled, setTrainingEnabled] = useState(false); // Training-Flag
  const [viewingDocument, setViewingDocument] = useState(null); // { id, filename }

  // Willkommensnachricht basierend auf Modus aktualisieren
  const getWelcomeMessage = (mode) => {
    if (mode === 'documents') {
      return 'Hallo! Ich kann Ihnen mit allgemeinen Fragen helfen und greife dabei auf Ihre hochgeladenen Dokumente und Gesprächsverlauf zurück.';
    }
    return 'Hallo! Ich bin Ihr KI-Assistent. Stellen Sie mir beliebige Fragen.';
  };

  // Modus ändern und Willkommensnachricht aktualisieren
  const handleModeChange = (newMode) => {
    setChatMode(newMode);
    // Training nur im Dokumentenmodus verfügbar
    if (newMode === 'free') {
      setTrainingEnabled(false);
    }
    // ConversationId bleibt erhalten beim Moduswechsel
    // Nur Sources/Snippets zurücksetzen (im freien Modus nicht relevant)
    setSources([]);
    setSnippets([]);
  };

  // Training-Flag sofort speichern
  const handleTrainingToggle = async (enabled) => {
    setTrainingEnabled(enabled);

    // Nur speichern wenn eine Konversation existiert
    if (conversationId) {
      try {
        await chatService.setTrainingFlag(conversationId, enabled);
        toast.success(
          enabled
            ? '✅ Konversation für KI-Training freigegeben'
            : '❌ Konversation vom KI-Training entfernt',
        );
      } catch (error) {
        // Bei Fehler Checkbox zurücksetzen
        setTrainingEnabled(!enabled);
        toast.error(`Fehler beim Speichern: ${error.message}`);
      }
    } else {
      // Wenn noch keine Konversation existiert, nur Toast anzeigen
      toast.success(
        enabled
          ? '✅ Training-Flag aktiviert (wird bei nächster Nachricht gespeichert)'
          : '❌ Training-Flag deaktiviert',
      );
    }
  };

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = { role: 'user', content: input };
    setMessages((prev) => [...prev, userMessage]);
    const messageToSend = input;
    setInput('');
    setIsLoading(true);
    setError(null);
    setSnippets([]);
    setSources([]);

    try {
      const result = await chatService.sendMessage(
        messageToSend,
        conversationId,
        chatMode,
        trainingEnabled,
      );
      const assistantMessage = { role: 'assistant', content: result.response.message };
      setMessages((prev) => [...prev, assistantMessage]);
      setSources(result.response.sources);

      // Snippets mit document_id anreichern durch Matching mit sources
      const enrichedSnippets = result.response.snippets.map((snippet) => {
        const matchingSource = result.response.sources.find(
          (source) => source.filename === snippet.filename,
        );
        return {
          ...snippet,
          document_id: matchingSource?.id || null,
        };
      });
      setSnippets(enrichedSnippets);

      if (result.response.conversationId) {
        setConversationId(result.response.conversationId);
      }
    } catch (err) {
      const errorMessageText = `Fehler: ${err.message}`;
      const errorMessage = { role: 'assistant', content: errorMessageText };
      setMessages((prev) => [...prev, errorMessage]);
      setError(errorMessageText);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDocument = (documentId, filename) => {
    setViewingDocument({ id: documentId, filename, searchText: null });
  };

  const handleViewSnippet = (documentId, filename, snippetText) => {
    setViewingDocument({ id: documentId, filename, searchText: snippetText });
  };

  const handleCloseDocumentViewer = () => {
    setViewingDocument(null);
  };

  return (
    <div
      className={`h-full flex flex-col p-4 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}
    >
      {/* Chat-Modus Umschaltung */}
      <div className="mb-4 flex justify-center">
        <div
          className={`flex rounded-lg p-1 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
        >
          <button
            onClick={() => handleModeChange('documents')}
            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
              chatMode === 'documents'
                ? `${isDarkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'} shadow-sm`
                : `${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`
            }`}
          >
            <FiDatabase className="mr-2" />
            Dokumentenmodus
          </button>
          <button
            onClick={() => handleModeChange('free')}
            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
              chatMode === 'free'
                ? `${isDarkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'} shadow-sm`
                : `${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`
            }`}
          >
            <FiMessageCircle className="mr-2" />
            Freier Modus
          </button>
        </div>
      </div>

      {/* Modus-Info */}
      <div className="mb-4 text-center">
        <div
          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
            chatMode === 'documents'
              ? `${isDarkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800'}`
              : `${isDarkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800'}`
          }`}
        >
          {chatMode === 'documents' ? (
            <>
              <FiDatabase className="mr-1" />
              Nutzt eigenes Wissen + hochgeladene Dokumente + Gesprächsverlauf
            </>
          ) : (
            <>
              <FiMessageCircle className="mr-1" />
              Reine KI-Unterstützung ohne Dokumentenzugriff
            </>
          )}
        </div>

        {/* Training-Toggle (nur im Dokumentenmodus) */}
        {chatMode === 'documents' && (
          <div className="mt-2 flex justify-center">
            <label
              className={`flex items-center cursor-pointer ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
            >
              <input
                type="checkbox"
                checked={trainingEnabled}
                onChange={(e) => handleTrainingToggle(e.target.checked)}
                className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm">💡 Diese Konversation für KI-Training freigeben</span>
            </label>
          </div>
        )}
      </div>

      {/* Conditional Layout basierend auf Chat-Modus */}
      {chatMode === 'documents' ? (
        // Dokumentenmodus: 3-Spalten-Layout mit Quellen und Snippets
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 flex-grow h-full min-h-0">
          {/* Linke Spalte: Chat - 2/3 der Breite */}
          <div className="lg:col-span-2 flex flex-col">
            <ChatWindow
              messages={messages}
              isLoading={isLoading}
              error={error}
              input={input}
              setInput={setInput}
              handleSendMessage={handleSendMessage}
              chatMode={chatMode}
            />
          </div>

          {/* Rechte Spalte: Quellen + Ausschnitte - 1/3 der Breite */}
          <div className="lg:col-span-1 flex flex-col gap-4 min-h-0">
            {/* Durchsuchte Dokumente oben */}
            <div className="flex-shrink-0" style={{ maxHeight: '30%' }}>
              <SourceList sources={sources} onViewDocument={handleViewDocument} />
            </div>

            {/* Relevante Ausschnitte unten - nimmt restlichen Platz */}
            <div className="flex-grow min-h-0">
              <SnippetDisplay snippets={snippets} onViewSnippet={handleViewSnippet} />
            </div>
          </div>
        </div>
      ) : (
        // Freier Modus: Zentriertes Layout ohne Seitenspalten (Gemini-Style)
        <div className="flex justify-center flex-grow h-full min-h-0">
          <div className="w-full max-w-4xl flex flex-col">
            <ChatWindow
              messages={messages}
              isLoading={isLoading}
              error={error}
              input={input}
              setInput={setInput}
              handleSendMessage={handleSendMessage}
              chatMode={chatMode}
            />
          </div>
        </div>
      )}

      {/* Document Viewer Modal */}
      {viewingDocument && (
        <DocumentViewer
          documentId={viewingDocument.id}
          filename={viewingDocument.filename}
          onClose={handleCloseDocumentViewer}
        />
      )}
    </div>
  );
}

export default Chat;
